import React, { useCallback } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import EditIcon from '@mui/icons-material/Edit';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Checkbox, Divider, IconButton, ListItemIcon, ListItemText, MenuItem, Tooltip } from '@mui/material';

import { useHiddenFileSelect } from '@paragon/client-tools';
import { ExtractedPageScale } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { hasPdfSegmentUploadAiScalingFeatureFlag } from '@paragon/login-tools';

import {
  exclusivelyShowSnappingUnderlay,
  hideSnappingUnderlay,
  showSnappingUnderlay,
  uploadDxfUnderlay,
} from '../../../store/actions/layout/snappingUnderlayActions';
import { handleStandardSceneItemEditPropertiesAction } from '../../../store/actions/sceneInteractionActions';
import { getAllSnappingUnderlays } from '../../../store/selectors/layoutDataSelectors';
import { isLayoutViewActive } from '../../../store/selectors/layoutModeSelectors';
import { getActiveUnderlayVisibility } from '../../../store/selectors/snappingGuideLineSelectors';
import { snappingUnderlaySlice } from '../../../store/slices/snappingUnderlaySlice';
import { setSnappingUnderlaysVisible } from '../../../store/slices/visualOptionsSlice';
import { isTouchEvent } from '../../../three/util/events/touch';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { ToolbarVerticalDivider } from '../top/ToolbarVerticalDivider';
import { RenderModeToolbarButton, useRenderModeToolbarMenuContext } from './RenderModeToolbarButton';

export function SnappingUnderlayButton() {
  const dispatch = useAppDispatch();
  const menuContext = useRenderModeToolbarMenuContext();

  const inLayoutMode = useAppSelector(isLayoutViewActive);
  const hasAiScalingFeatureFlag = useAppSelector(hasPdfSegmentUploadAiScalingFeatureFlag);
  const snappingUnderlaysVisible = useAppSelector((state) => state.visualOptions.snappingUnderlaysVisible);
  const underlayList = useAppSelector(getAllSnappingUnderlays);
  const effectivelyVisible = useAppSelector(getActiveUnderlayVisibility);

  const [renderFileSelect, selectFile] = useHiddenFileSelect('.pdf,.dxf', false);

  const triggerUnderlayUpload = useCallback(() => {
    selectFile()
      .then(async (file) => {
        if (file.filetype === 'dxf') {
          await dispatch(uploadDxfUnderlay(file));
          return;
        }

        const pdfUrl = file.contents;
        const pdfFileName = file.filename;

        let extractedScales: ExtractedPageScale[] = [];

        if (hasAiScalingFeatureFlag) {
          dispatch(snappingUnderlaySlice.actions.uploadBegan());
          extractedScales = await designService.pdfs.extractScale(pdfUrl).catch(() => []);
        }

        dispatch(snappingUnderlaySlice.actions.pdfUploadCompleted({ pdfUrl, pdfFileName, extractedScales }));
      })
      .catch((error) => {
        console.error(error);
        dispatch(snappingUnderlaySlice.actions.uploadFailed());
      });
  }, [selectFile, hasAiScalingFeatureFlag, dispatch]);

  if (!inLayoutMode) return null;

  function closeMenuAfterItemSelectionIfNeeded(event: React.MouseEvent) {
    if (isTouchEvent(event.nativeEvent)) {
      menuContext.closeActiveMenu();
    }
  }

  return (
    <RenderModeToolbarButton
      menuKey="snappingUnderlays"
      icon={<DocumentScannerIcon />}
      title="Snapping Underlays"
      onMouseClick={() => {
        dispatch(setSnappingUnderlaysVisible(!snappingUnderlaysVisible));
      }}
      onWheel={() => {
        dispatch(setSnappingUnderlaysVisible(!snappingUnderlaysVisible));
      }}
      afterButton={
        <>
          {renderFileSelect()}
          <ToolbarVerticalDivider />
        </>
      }
    >
      <MenuItem disabled>Snapping Underlays</MenuItem>

      <MenuItem
        selected={snappingUnderlaysVisible}
        onClick={(event) => {
          dispatch(setSnappingUnderlaysVisible(true));
          closeMenuAfterItemSelectionIfNeeded(event);
        }}
      >
        {snappingUnderlaysVisible && (
          <ListItemIcon>
            <CheckIcon />
          </ListItemIcon>
        )}
        <ListItemText inset={!snappingUnderlaysVisible}>Visible</ListItemText>
      </MenuItem>

      <MenuItem
        selected={!snappingUnderlaysVisible}
        onClick={(event) => {
          dispatch(setSnappingUnderlaysVisible(false));
          closeMenuAfterItemSelectionIfNeeded(event);
        }}
      >
        {!snappingUnderlaysVisible && (
          <ListItemIcon>
            <CheckIcon />
          </ListItemIcon>
        )}
        <ListItemText inset={snappingUnderlaysVisible}>Hidden</ListItemText>
      </MenuItem>

      <Divider component="li" sx={{ marginY: '4px!important' }} />

      <MenuItem
        onClick={(event) => {
          triggerUnderlayUpload();
          closeMenuAfterItemSelectionIfNeeded(event);
        }}
      >
        <ListItemIcon>
          <FileUploadIcon />
        </ListItemIcon>
        <ListItemText>Upload PDF or DXF</ListItemText>
      </MenuItem>

      {underlayList.length > 0 && [
        <Divider key="divider" component="li" sx={{ marginY: '4px!important' }} />,

        underlayList.map((underlay) => {
          const visible = effectivelyVisible(underlay.guid);

          return (
            <MenuItem
              key={underlay.guid}
              onClick={() => {
                dispatch(exclusivelyShowSnappingUnderlay(underlay.guid));
              }}
            >
              <ListItemIcon>
                <Checkbox
                  checked={visible}
                  sx={{ margin: -1 }}
                  onClick={(event) => {
                    event.stopPropagation(); // don't bubble to menu item
                  }}
                  onChange={(_, checked) => {
                    if (checked) {
                      dispatch(showSnappingUnderlay(underlay.guid));
                    } else {
                      dispatch(hideSnappingUnderlay(underlay.guid));
                    }
                  }}
                />
              </ListItemIcon>

              <ListItemText>{underlay.name}</ListItemText>

              <Tooltip title="Configure Underlay">
                <IconButton
                  size="small"
                  sx={{ marginLeft: 1 }}
                  onClick={(event) => {
                    event.stopPropagation();
                    menuContext.closeActiveMenu();

                    dispatch(handleStandardSceneItemEditPropertiesAction('snappingUnderlay', underlay));
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </MenuItem>
          );
        }),
      ]}
    </RenderModeToolbarButton>
  );
}
